import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CONTACT_EMAIL } from './ParentConsentForm';

// The page a parent reaches from the email: explains the app, links the form, takes the upload.
const ParentConsent = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [missing, setMissing] = useState(false);
  const [file, setFile] = useState(null);
  const [typed, setTyped] = useState('');
  const [voiceOk, setVoiceOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetch('/api/v1/parent-consent/' + encodeURIComponent(token))
      .then(r => { if (!r.ok) throw new Error('missing'); return r.json(); })
      .then(setInfo).catch(() => setMissing(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) { setError('Please choose the signed form to upload.'); return; }
    if (!typed.trim()) { setError('Please type your full name.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('parent_typed_name', typed.trim());
      fd.append('voice_ok', voiceOk ? 'true' : 'false');
      const r = await fetch('/api/v1/parent-consent/' + encodeURIComponent(token) + '/upload', { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((d && d.detail) || 'Upload failed. Please try again.');
      setDone(true);
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const page = { minHeight: '100vh', background: '#5b9bd5', padding: '24px 16px', display: 'flex', justifyContent: 'center', fontFamily: 'Georgia, serif' };
  const card = { width: '100%', maxWidth: '560px', background: '#fff', borderRadius: '18px', padding: '30px 26px', boxShadow: '0 20px 60px rgba(30,58,95,0.18)', alignSelf: 'flex-start', boxSizing: 'border-box', color: '#1e3a5f', fontSize: '16px', lineHeight: 1.55 };
  const btn = { width: '100%', minHeight: '50px', borderRadius: '10px', border: 'none', background: '#4f46e5', color: '#fff', fontSize: '17px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  const label = { display: 'block', fontWeight: 700, margin: '20px 0 8px' };
  const first = info && info.child_name ? info.child_name.split(' ')[0] : 'your child';

  if (missing) return (<div style={page}><div style={card}><h1 style={{ fontSize: '24px', marginTop: 0 }}>This link is not valid</h1>
    <p>Please use the most recent email we sent you, or write to {CONTACT_EMAIL}.</p></div></div>);
  if (!info) return (<div style={page}><div style={card}><p>Loading...</p></div></div>);
  if (info.status === 'approved') return (<div style={page}><div style={card}><h1 style={{ fontSize: '24px', marginTop: 0 }}>Permission approved</h1>
    <p>Thank you. {first}'s account is open. To see, delete or close {first}'s information at any time, write to {CONTACT_EMAIL}.</p></div></div>);
  if (done || info.status === 'submitted') return (<div style={page}><div style={card}><h1 style={{ fontSize: '24px', marginTop: 0 }}>Thank you</h1>
    <p>We received your signed form. We will review it and email you when {first}'s account is open, usually within one business day.</p>
    {info.status === 'submitted' && !done && <p style={{ fontSize: '15px', color: '#4b5563' }}>Need to replace the form? Upload a new one below.</p>}
    {info.status === 'submitted' && !done && <button type="button" style={{ ...btn, background: '#1e3a5f' }} onClick={() => setInfo({ ...info, status: 'awaiting_form' })}>Upload a new form</button>}
  </div></div>);

  return (
    <div style={page}>
      <form style={card} onSubmit={submit}>
        <h1 style={{ fontSize: '25px', margin: '0 0 6px' }}>Permission for {first}</h1>
        {info.status === 'rejected' && (
          <p style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 12px', borderRadius: '8px' }}>
            We could not accept the last form. Please upload a new signed form.
          </p>
        )}
        <p>{first} has asked to use the FHO Platform, an AI coaching app. Because {first} is under 14, the account stays closed until you give written permission.</p>
        <ol style={{ paddingLeft: '22px', margin: '14px 0' }}>
          <li>Open the consent form. It explains what the app does, what it keeps, and your rights.</li>
          <li>Print it, then sign and date it. No printer? Save it as a PDF and sign it on your phone or computer.</li>
          <li>Upload a photo or PDF of the signed form below.</li>
        </ol>
        <Link to={'/parent-consent/' + token + '/form'} target="_blank" rel="noopener noreferrer"
          style={{ display: 'block', textAlign: 'center', padding: '13px', borderRadius: '10px', border: '2px solid #4f46e5', color: '#4f46e5', fontWeight: 700, textDecoration: 'none', marginBottom: '6px' }}>
          Open the consent form
        </Link>

        <label style={label} htmlFor="consent-file">Signed form (PDF, JPG or PNG, up to 10 MB)</label>
        <input id="consent-file" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={e => setFile(e.target.files && e.target.files[0])} style={{ fontSize: '16px', maxWidth: '100%' }} />

        <label style={label} htmlFor="consent-name">Your full name</label>
        <input id="consent-name" type="text" value={typed} maxLength={80} onChange={e => setTyped(e.target.value)}
          style={{ width: '100%', padding: '12px 14px', fontSize: '16px', borderRadius: '10px', border: '1px solid rgba(30,58,95,0.3)', boxSizing: 'border-box', fontFamily: 'inherit' }} />

        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', margin: '20px 0 6px' }}>
          <input id="consent-voice" type="checkbox" checked={voiceOk} onChange={e => setVoiceOk(e.target.checked)}
            style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '3px', accentColor: '#4f46e5' }} />
          <label htmlFor="consent-voice" style={{ cursor: 'pointer' }}>Voice: I allow ElevenLabs to turn the coach's replies into speech (the same choice as on the form). Leave unchecked to use only the device's own voice.</label>
        </div>

        {error && <p style={{ color: '#991b1b', margin: '12px 0' }}>{error}</p>}
        <button type="submit" disabled={busy} style={{ ...btn, marginTop: '16px', opacity: busy ? 0.7 : 1 }}>{busy ? 'Uploading...' : 'Upload signed form'}</button>
        <p style={{ fontSize: '14px', color: '#4b5563', marginTop: '16px' }}>Questions, or to withdraw permission later: {CONTACT_EMAIL}</p>
      </form>
    </div>
  );
};

export default ParentConsent;
